import { ReactElement, useEffect, ChangeEvent, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { XCircle } from 'react-feather';
import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { getLoggedRole } from 'data-layer-cr/dist/authentication';
import {
  HeaderText,
  H4Text,
  DisplayItem,
  RadioButtonGroup,
  DottedHr,
  colors,
  NetworkIndicator,
} from 'design-system';
import { DropdownCheckbox } from 'src/components/Inputs';
import mapToCatalogItem from 'src/functions/mapToCatalogItem';
import { RootState } from 'src/store/rootReducer';
import { getLicenses } from 'src/store/profile/actions';
import { getSubagents } from 'src/store/quotation/actions';
import { GET_LICENSES } from 'src/store/profile/requestIdentifiers';
import {
  FilterCategory,
  FilterOption,
  FilterOptionCategoryId,
  DEFAULT_COMPONENT_TYPE,
  defaultDate,
  rangeDate,
  DROPDOWN_CHECKBOX_TYPE,
} from 'src/models/Filter';
import {
  mapForSingularFilterId,
  mapLicenseSelectedValues,
  defineSubagentState,
  isQueriableSubagent,
} from './filterDrawerHelpers';
import categoryTitleReducer from './categoryTitleReducer';

import DateRangePicker from '../DateRangePicker';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerPaper: {
      width: theme.spacing(50.5),
    },
    container: {
      display: 'flex',
      flexFlow: 'column',
      padding: theme.spacing(2, 4),
    },
    lisenceSection: {
      marginTop: 10,
      display: 'flex',
      flexFlow: 'column',
    },
    categorySection: {
      margin: theme.spacing(2, 0),
      display: 'flex',
      flexFlow: 'column',
    },
    navBarContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: theme.spacing(6.75),
      padding: theme.spacing(1, 4),
    },
    buttonDateRangeFilter: {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 4,
      textTransform: 'capitalize',
      color: colors.madison,
      backgroundColor: 'transparent',
      fontWeight: 500,
      fontSize: '12px',
    },
    checkboxItem: {
      marginTop: 0,
      marginRight: 4,
    },
  }),
);

interface Props extends PropsFromRedux {
  open: boolean;
  onClose: () => void;
  filterCatalog: FilterCategory[];
  singularCategoriesFilterMap: Map<string, FilterOption>;
  multipleCategoriesFilterMap: Map<string, Map<string, FilterOption>>;
  onAddFilter: (filter: FilterOption) => void;
  onRemoveFilter: (filter: FilterOption) => void;
}

function mapToRadioDisplayItem(
  filterList: FilterOption[],
): DisplayItem<FilterOption>[] {
  return filterList
    .filter(({ componentType }) => componentType === DEFAULT_COMPONENT_TYPE)
    .map((filter) => ({
      Value: filter.id,
      Text: filter.description,
      Object: filter,
    }));
}

function makeDottedSection(): JSX.Element {
  return (
    <div style={{ width: '100%' }}>
      <DottedHr />
    </div>
  );
}

function generateFilterOption(
  selected: string,
  description: string,
  category: FilterOptionCategoryId,
): FilterOption {
  return {
    id: selected,
    categoryId: category,
    description,
    queryKey: selected,
    componentType: DROPDOWN_CHECKBOX_TYPE,
  };
}

export function FilterDrawer({
  open,
  onClose,
  filterCatalog,
  singularCategoriesFilterMap,
  multipleCategoriesFilterMap,
  onAddFilter,
  onRemoveFilter,
  licenses,
  getLicensesAction,
  getSubagentsAction,
  subAgentList,
}: Props): ReactElement {
  const classes = useStyles();
  const licenseRequestStatus = useNetworkObserver(GET_LICENSES);
  const isUserRoleASE = getLoggedRole() === 'ASE';
  const [canFilterSubagent, setCanFilterSubagent] = useState(false);

  const handleAddDateRangeFilterOption = (
    description: string,
    startDate: Date,
    endDate: Date,
  ) => {
    onAddFilter({
      ...rangeDate,
      id: 'range-date',
      categoryId: 'date',
      description,
      dates: {
        start: startDate,
        end: endDate,
      },
    });
  };

  const handleResetDateRangeFilterOption = () => onAddFilter(defaultDate);

  useEffect(() => {
    if (licenses.length === 0) {
      getLicensesAction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSubagentFilter = useMemo(() => {
    return (
      (canFilterSubagent && subAgentList.length > 0) ||
      defineSubagentState(singularCategoriesFilterMap)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    subAgentList.length,
    canFilterSubagent,
    singularCategoriesFilterMap.size,
  ]);

  const handleCheckBoxChange = (checked: boolean, option: FilterOption) => {
    if (checked) {
      onAddFilter(option);
      return;
    }
    onRemoveFilter(option);
  };

  const isCheckBoxActive = (categoryId: string, optionId: string): boolean => {
    const categoryMap = multipleCategoriesFilterMap.get(categoryId);
    return categoryMap !== undefined && categoryMap.get(optionId) !== undefined;
  };

  const handleChangeLicense = (selected: string, description: string) => {
    const filterSubagent = singularCategoriesFilterMap.get('subagent');
    if (filterSubagent) {
      onRemoveFilter(filterSubagent);
    }
    const isQueriableSubagentResult = isQueriableSubagent(
      selected,
      isUserRoleASE,
    );
    setCanFilterSubagent(isQueriableSubagentResult);
    if (isQueriableSubagentResult) {
      getSubagentsAction(selected);
    }
    const filterOption = generateFilterOption(selected, description, 'license');
    onAddFilter(filterOption);
  };

  const handleChangeSubagent = (selected: string, description: string) => {
    const filterOption = generateFilterOption(
      selected,
      description,
      'subagent',
    );
    onAddFilter(filterOption);
  };

  return (
    <Drawer
      data-testid="drawer-search"
      anchor="right"
      open={open}
      onClose={onClose}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      {/* Navbar section */}
      <div style={{ backgroundColor: colors.madison }}>
        <div className={classes.navBarContent}>
          <HeaderText style={{ color: '#fff' }}>Filtros</HeaderText>
          <IconButton onClick={onClose} data-testid="close-button">
            <XCircle strokeWidth="1px" color="#fff" />
          </IconButton>
        </div>
      </div>
      {/* Singular type filter categories */}
      <div className={classes.container}>
        {filterCatalog.map((category) => {
          if (category.type === 'singular') {
            const radioData = mapToRadioDisplayItem(category.options);
            if (category.id === 'license') {
              const currentValues = mapForSingularFilterId(
                'license',
                singularCategoriesFilterMap,
              );
              return (
                <div
                  key={`singular-type-${category.id}`}
                  className={classes.lisenceSection}
                >
                  <DropdownCheckbox
                    data-testid="license-dropdown"
                    id="sales-web-dropdown-license-filter"
                    optionList={mapToCatalogItem(licenses)}
                    currentValues={mapLicenseSelectedValues(currentValues)}
                    applyChanges={handleChangeLicense}
                    showDetails
                    titleText="Licencia de corredor"
                  />
                  <NetworkIndicator
                    data-testid="network-indicator"
                    requesting={licenseRequestStatus === 'requesting'}
                    message="Solicitando lista de licencias..."
                  />
                </div>
              );
            }

            if (category.id === 'subagent') {
              const currentValues = mapForSingularFilterId(
                'subagent',
                singularCategoriesFilterMap,
              );
              return showSubagentFilter ? (
                <div
                  key={`singular-type-${category.id}`}
                  className={classes.lisenceSection}
                >
                  <DropdownCheckbox
                    data-testid="subagent-dropdown"
                    id="sales-web-dropdown-subagent-filter"
                    optionList={mapToCatalogItem(subAgentList)}
                    currentValues={mapLicenseSelectedValues(currentValues)}
                    applyChanges={handleChangeSubagent}
                    element="Subagentes"
                    showDetails
                    titleText="Selecciona un subagente (opcional)"
                  />
                </div>
              ) : (
                <></>
              );
            }

            return (
              <div
                key={`singular-type-${category.id}`}
                className={classes.categorySection}
              >
                <H4Text style={{ fontWeight: 'bold' }}>
                  {categoryTitleReducer(category.id)}
                </H4Text>
                {makeDottedSection()}
                {category.id === 'date' && (
                  <DateRangePicker
                    data-testid="date-range-picker"
                    onAddFilter={handleAddDateRangeFilterOption}
                    onReset={handleResetDateRangeFilterOption}
                    descriptionWhenSelected={
                      singularCategoriesFilterMap.get('date')
                        ?.description as string
                    }
                    descriptionWhenNotSelected="Personalizado"
                    buttonClassName={classes.buttonDateRangeFilter}
                    isSelected={
                      rangeDate.id ===
                      mapForSingularFilterId(
                        category.id,
                        singularCategoriesFilterMap,
                      )
                    }
                  />
                )}
                <div style={{ marginLeft: '-10px' }}>
                  <RadioButtonGroup
                    data-testid={`radio-group-${category.id}`}
                    inputId={`sales-web-radio-filter${category.id}`}
                    formTitle=""
                    gridSize={12}
                    radioData={radioData}
                    radioGroupName={`radio-group-${category.id}`}
                    defaultValue={mapForSingularFilterId(
                      category.id,
                      singularCategoriesFilterMap,
                    )}
                    onChange={(filter) => {
                      const filterObject = filter.Object as FilterOption;
                      onAddFilter(filterObject);
                    }}
                  />
                </div>
              </div>
            );
          }
          // Multiple type filter categories
          return (
            <div
              key={`multiple-type-${category.id}`}
              className={classes.categorySection}
              data-testid={`checkbox-group-${category.id}`}
            >
              <H4Text style={{ fontWeight: 'bold' }}>
                {categoryTitleReducer(category.id)}
              </H4Text>
              {makeDottedSection()}
              {category.options.map((option) => {
                const isActive = isCheckBoxActive(option.categoryId, option.id);
                return (
                  <FormControl
                    key={`checkbox-${option.id}`}
                    style={{
                      display: 'inline-block',
                      padding: 0,
                      marginRight: 4,
                    }}
                  >
                    <FormControlLabel
                      style={{ margin: 0, marginTop: 8 }}
                      key={`checkbox-${option.id}`}
                      id={`sales-web-checkbox-filter${option.id}`}
                      control={<Checkbox className={classes.checkboxItem} />}
                      checked={isActive}
                      onChange={(_event: ChangeEvent<{}>, checked: boolean) => {
                        handleCheckBoxChange(checked, option);
                      }}
                      label={option.description}
                    />
                  </FormControl>
                );
              })}
            </div>
          );
        })}
      </div>
    </Drawer>
  );
}

export const mapStateToProps = (
  state: RootState,
): {
  licenses: GeneralCatalogItem[];
  subAgentList: GeneralCatalogItem[];
} => ({
  licenses: state.profile.licenses,
  subAgentList: state.quotation.subAgentList,
});
const reduxActions = {
  getLicensesAction: getLicenses,
  getSubagentsAction: getSubagents,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(FilterDrawer);
